import React, { useState, useEffect, useRef } from 'react';
import { Calculator, TrendingUp, DollarSign, Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const ForecastCalculator = () => {
  const [investment, setInvestment] = useState(10000);
  const [months, setMonths] = useState(12);
  const [projectionData, setProjectionData] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const MONTHLY_GROWTH_RATE = 0.05; // 5% monthly average

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Calculate projection whenever investment or months change
  useEffect(() => {
    const data = [];
    let currentValue = investment;
    
    data.push({
      month: 'Start',
      value: currentValue,
      monthNum: 0
    });

    for (let i = 1; i <= months; i++) {
      currentValue = currentValue * (1 + MONTHLY_GROWTH_RATE);
      data.push({
        month: `M${i}`,
        value: Math.round(currentValue),
        monthNum: i
      });
    }

    setProjectionData(data);
  }, [investment, months]);

  const finalValue = projectionData.length > 0 ? projectionData[projectionData.length - 1].value : investment;
  const totalGrowth = finalValue - investment;
  const percentageGrowth = ((finalValue - investment) / investment * 100).toFixed(1);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-black/95 border border-white/10 backdrop-blur-sm px-3 py-2 rounded text-xs">
          <p className="text-white/50 mb-1">{payload[0].payload.month}</p>
          <p className="text-green-400 font-medium">
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  const presetAmounts = [1000, 5000, 10000, 25000, 50000, 100000];

  return (
    <section ref={sectionRef} className="relative bg-[#050505] py-16 md:py-24 lg:py-32 overflow-hidden">
      {/* Background Chart Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=2940&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-[0.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/95 to-[#050505]" />
      </div>
      
      {/* Ambient Effects */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-radial from-green-900/10 via-transparent to-transparent blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-gradient-radial from-amber-900/5 via-transparent to-transparent blur-3xl" />
      
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16">
        {/* Header */}
        <div 
          className="mb-8 md:mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center">
              <Calculator className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
            </div>
            <p className="text-white/30 text-[10px] md:text-xs font-medium tracking-[0.2em] md:tracking-[0.3em]">
              [ GROWTH FORECAST ]
            </p>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-light text-white leading-tight tracking-tight">
            Project Your Returns
          </h2>
          <p className="text-white/40 text-xs md:text-sm font-light mt-2 max-w-lg">
            See potential growth based on 5% average monthly performance
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Left - Calculator Controls */}
          <div 
            className="lg:col-span-2"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 200ms'
            }}
          >
            <div className="bg-black/40 border border-white/[0.06] rounded-lg p-4 md:p-6">
              {/* Investment Amount */}
              <div className="mb-6">
                <label className="block text-white/40 text-[10px] md:text-xs font-medium tracking-wider mb-2 md:mb-3">
                  INVESTMENT AMOUNT
                </label>
                <div className="relative mb-3">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="number"
                    min="100"
                    max="100000"
                    value={investment}
                    onChange={(e) => setInvestment(Math.min(100000, Math.max(100, Number(e.target.value))))}
                    className="w-full bg-white/[0.03] border border-white/10 rounded px-8 py-2.5 md:py-3 text-white text-base md:text-lg font-light focus:outline-none focus:border-green-500/50 transition-colors"
                  />
                </div>
                {/* Preset Buttons */}
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {presetAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setInvestment(amount)}
                      className={`px-2 md:px-3 py-1 md:py-1.5 text-[10px] md:text-xs font-medium rounded transition-all duration-300 ${
                        investment === amount
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-white/[0.03] text-white/40 border border-white/10 hover:border-white/20'
                      }`}
                    >
                      {amount >= 1000 ? `$${amount/1000}k` : `$${amount}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Duration */}
              <div className="mb-6">
                <label className="block text-white/40 text-[10px] md:text-xs font-medium tracking-wider mb-2 md:mb-3">
                  DURATION (MONTHS)
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="range"
                    min="3"
                    max="24"
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    className="w-full h-1.5 md:h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-green-500"
                  />
                  <div className="flex justify-between mt-2 text-[10px] md:text-xs text-white/30">
                    <span>3 mo</span>
                    <span className="text-green-400 font-medium">{months} months</span>
                    <span>24 mo</span>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3 md:p-4">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <p className="text-white/40 text-[10px] md:text-xs mb-1">Projected Value</p>
                    <p className="text-xl md:text-2xl font-light text-green-400">
                      {formatCurrency(finalValue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/40 text-[10px] md:text-xs mb-1">Total Growth</p>
                    <p className="text-xl md:text-2xl font-light text-white">
                      +{percentageGrowth}%
                    </p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-green-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-white/40 text-[10px] md:text-xs">Profit</span>
                    <span className="text-green-400 text-sm md:text-base font-medium">
                      +{formatCurrency(totalGrowth)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Chart */}
          <div 
            className="lg:col-span-3"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(40px)',
              transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 400ms'
            }}
          >
            <div className="bg-black/40 border border-white/[0.06] rounded-lg p-4 md:p-6 h-full">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <h4 className="text-white text-sm md:text-base font-light">Growth Projection</h4>
                </div>
                <span className="text-green-400 text-[10px] md:text-xs">5% monthly</span>
              </div>
              
              {/* Chart */}
              <div className="h-[200px] md:h-[250px] lg:h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={projectionData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="month" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 9 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      domain={['auto', 'auto']}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 9 }}
                      tickFormatter={(value) => value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`}
                      width={45}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#22c55e"
                      strokeWidth={2}
                      fill="url(#forecastGradient)"
                      animationDuration={1500}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Key Info */}
              <div className="mt-4 pt-4 border-t border-white/[0.06] grid grid-cols-3 gap-2 md:gap-4">
                <div className="text-center">
                  <p className="text-white/30 text-[9px] md:text-[10px] mb-0.5">Start</p>
                  <p className="text-white text-xs md:text-sm font-light">{formatCurrency(investment)}</p>
                </div>
                <div className="text-center">
                  <p className="text-white/30 text-[9px] md:text-[10px] mb-0.5">Monthly Rate</p>
                  <p className="text-amber-400 text-xs md:text-sm font-light">+5%</p>
                </div>
                <div className="text-center">
                  <p className="text-white/30 text-[9px] md:text-[10px] mb-0.5">End Value</p>
                  <p className="text-green-400 text-xs md:text-sm font-light">{formatCurrency(finalValue)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Disclaimer */}
        <div 
          className="mt-4 md:mt-6"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 600ms'
          }}
        >
          <div className="bg-amber-500/5 border border-amber-500/20 rounded px-3 md:px-4 py-2 md:py-3">
            <p className="text-amber-500/70 text-[9px] md:text-[10px] leading-relaxed">
              <span className="font-medium">Risk Disclaimer:</span> This is not a guaranteed return. This projection is a forecast based on past performance (5% average monthly growth) and is not guaranteed. Actual results may vary. Past performance does not guarantee future results. Trading involves risk and you may lose some or all of your investment.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForecastCalculator;
